import { apiService } from '@/api/apiConfig';
import {
  InviteBudgetMemberPayload,
  UpdateBudgetMemberPayload,
  UpdateMemberRolePayload,
  BulkUpdateMembersPayload,
  BulkUpdateMembersData,
  BulkUpdateMembersResponse,
  ValidateMemberEmailPayload,
  ValidateMemberEmailData,
  ValidateMemberEmailResponse,
  BudgetMembersResponse,
  BudgetMemberResponse
} from '@/services/types/budget-members.types';

const handleBudgetMemberError = (error: any): Error => {
  if (error instanceof Error && error.message) {
    return error;
  }
  if (error.response?.data?.message) {
    return new Error(error.response.data.message);
  }
  return new Error('Budget member operation failed');
};

export const budgetMemberService = {
  /** POST /budget/members/validate-email */
  validateMemberEmail: async (payload: ValidateMemberEmailPayload): Promise<ValidateMemberEmailData> => {
    try {
      const response = await apiService.request<ValidateMemberEmailResponse>({
        method: 'POST',
        url: '/budget/members/validate-email',
        data: payload,
        headers: { 'Content-Type': 'application/json' },
        skipErrorToast: true
      });
      return response.data;
    } catch (error) {
      throw handleBudgetMemberError(error);
    }
  },

  /** GET /budget/:budgetId/members */
  getMembers: async (budgetId: string): Promise<BudgetMembersResponse> => {
    try {
      return await apiService.request<BudgetMembersResponse>({
        method: 'GET',
        url: `/budget/${budgetId}/members`
      });
    } catch (error) {
      throw handleBudgetMemberError(error);
    }
  },

  /** POST /budget/:budgetId/members/invite */
  inviteMember: async (payload: InviteBudgetMemberPayload): Promise<BudgetMemberResponse> => {
    try {
      const { budgetId, ...data } = payload;
      return await apiService.request<BudgetMemberResponse>({
        method: 'POST',
        url: `/budget/${budgetId}/members/invite`,
        data,
        headers: { 'Content-Type': 'application/json' },
        skipErrorToast: true
      });
    } catch (error) {
      throw handleBudgetMemberError(error);
    }
  },

  /** PATCH /budget/:budgetId/members/:memberId */
  updateMember: async (
    budgetId: string,
    memberId: string,
    payload: UpdateBudgetMemberPayload
  ): Promise<BudgetMemberResponse> => {
    try {
      return await apiService.request<BudgetMemberResponse>({
        method: 'PATCH',
        url: `/budget/${budgetId}/members/${memberId}`,
        data: payload,
        headers: { 'Content-Type': 'application/json' }
      });
    } catch (error) {
      throw handleBudgetMemberError(error);
    }
  },

  /** PATCH /budget/:budgetId/members/:memberId/role */
  updateMemberRole: async (
    budgetId: string,
    memberId: string,
    payload: UpdateMemberRolePayload
  ): Promise<BudgetMemberResponse> => {
    try {
      return await apiService.request<BudgetMemberResponse>({
        method: 'PATCH',
        url: `/budget/${budgetId}/members/${memberId}/role`,
        data: payload,
        headers: { 'Content-Type': 'application/json' },
        skipErrorToast: true
      });
    } catch (error) {
      throw handleBudgetMemberError(error);
    }
  },

  /** PATCH /budget/:budgetId/members (Manage Members page Save action) */
  updateMembers: async (budgetId: string, payload: BulkUpdateMembersPayload): Promise<BulkUpdateMembersData> => {
    try {
      const response = await apiService.request<BulkUpdateMembersResponse>({
        method: 'PATCH',
        url: `/budget/${budgetId}/members`,
        data: payload,
        headers: { 'Content-Type': 'application/json' }
      });
      return response.data;
    } catch (error) {
      throw handleBudgetMemberError(error);
    }
  },

  /** DELETE /budget/:budgetId/members/:memberId */
  removeMember: async (budgetId: string, memberId: string): Promise<void> => {
    try {
      return await apiService.request<void>({
        method: 'DELETE',
        url: `/budget/${budgetId}/members/${memberId}`,
        skipErrorToast: true
      });
    } catch (error) {
      throw handleBudgetMemberError(error);
    }
  }
};
